import { useContext } from 'react';
import { AuthContext } from '@contexts/auth/auth-context';
import { useMutation } from '@tanstack/react-query';
import { RequestError } from '@utils/request/request.type';

import { AuthTokenResponse } from './type';
import { requireRefreshedToken } from './use-get-tokens';
import { useTokenManagement } from './use-token-management';

/**
 * Custom hook to handle refresh token logic and retry failed requests
 * Provides a factory function to create refresh-and-retry handlers for queries and mutations
 */
export const useRefreshTokenHandler = () => {
  const { getTokens, setTokens } = useTokenManagement();
  const { refreshToken } = getTokens();
  const { mutateAsync: refreshTokensAsync } = useMutation({
    mutationFn: () => requireRefreshedToken(refreshToken ?? ''),
  });
  const { signOut } = useContext(AuthContext);

  /**
   * Creates a handler that refreshes the token and retries the original request
   * @param retryFn - The function to retry after refreshing the token
   * @returns A handler function that executes the refresh-and-retry flow
   */
  const createRefreshAndRetryHandler = <TContext, TReturn>(
    retryFn: (context: TContext) => TReturn | Promise<TReturn>
  ) => {
    return async (context: TContext): Promise<Awaited<TReturn>> => {
      if (!refreshToken) {
        signOut();
        throw new Error('No refresh token');
      }

      let tokens: AuthTokenResponse;

      // Step 1: Try to refresh the token
      try {
        tokens = await refreshTokensAsync();
        setTokens({ accessToken: tokens.accessToken, refreshToken: tokens.refreshToken });
      } catch (refreshError: unknown) {
        // If REFRESH TOKEN REQUEST fails, sign out
        signOut();
        throw refreshError;
      }

      // Step 2: Retry the original request with the new token
      try {
        const result = retryFn(context);
        return await Promise.resolve(result);
      } catch (retryError: unknown) {
        // If retry fails with 401 again, refresh token is invalid
        if (retryError instanceof RequestError && retryError.status === 401) {
          signOut();
        }
        throw retryError;
      }
    };
  };

  return { createRefreshAndRetryHandler, refreshToken };
};
